// FirewallServiceObjects.tsx
import { useState, useEffect, useCallback } from "react";
import {
    RefreshCw, Loader2, Search, Layers,
    ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
    ArrowUp, ArrowDown, ArrowLeft, Info,
    Activity, Clock,
} from "lucide-react";
import baseapi from "../../../../../services/baseapi";
import { showError } from "../../../../../utils/toast";
import { ItemsPerPageDropdown } from "../../../../../components/ItemsPerPageDropdown ";

// ── Types ─────────────────────────────────────────────────────────────────────
interface ServiceObjectData {
    name: string;
    protocol: string;
    category: string;
    comment: string;
    uuid: string;
    "uuid-idx": number;
    proxy: string;
    helper: string;
    iprange: string;
    fqdn: string;
    "tcp-portrange"?: string;
    "udp-portrange"?: string;
    "sctp-portrange"?: string;
    "session-ttl": string;
    "fabric-object": string;
    icmptype?: number;
    icmpcode?: string;
    "app-service-type": string;
    "tcp-rst-timer": number;
    "tcp-halfopen-timer": number;
    "tcp-timewait-timer": number;
    "tcp-halfclose-timer": number;
    "udp-idle-timer": number;
    application: any[];
    "app-category": any[];
}

interface ServiceObjectItem {
    id: number;
    created: string;
    updated: string;
    asset_id: number;
    firewall_type: string;
    name: string;
    data: any;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const parseData = (raw: any): ServiceObjectData | null => {
    if (typeof raw === "object" && raw !== null) {
        return raw as ServiceObjectData;
    }
    try { return JSON.parse(raw); } catch { return null; }
};

const fmtPorts = (ports: string | undefined): string[] => {
    if (!ports || ports.trim() === "") return [];
    return ports.trim().split(/\s+/).filter(Boolean);
};

// ── Protocol Badge ────────────────────────────────────────────────────────────
const PROTO_STYLES: Record<string, string> = {
    "TCP/UDP/SCTP": "bg-blue-50 dark:bg-blue-500/10 border-blue-200 dark:border-blue-500/20 text-blue-700 dark:text-blue-400",
    "TCP": "bg-cyan-50 dark:bg-cyan-500/10 border-cyan-200 dark:border-cyan-500/20 text-cyan-700 dark:text-cyan-400",
    "UDP": "bg-green-50 dark:bg-green-500/10 border-green-200 dark:border-green-500/20 text-green-700 dark:text-green-400",
    "SCTP": "bg-purple-50 dark:bg-purple-500/10 border-purple-200 dark:border-purple-500/20 text-purple-700 dark:text-purple-400",
    "ICMP": "bg-orange-50 dark:bg-orange-500/10 border-orange-200 dark:border-orange-500/20 text-orange-700 dark:text-orange-400",
    "ICMP6": "bg-amber-50 dark:bg-yellow-500/10 border-amber-200 dark:border-yellow-500/20 text-amber-700 dark:text-yellow-400",
    "IP": "bg-slate-100 dark:bg-gray-500/10 border-slate-200 dark:border-gray-500/20 text-slate-600 dark:text-gray-400",
    "ALL": "bg-rose-50 dark:bg-rose-500/10 border-rose-200 dark:border-rose-500/20 text-rose-700 dark:text-rose-400",
};

const ProtoBadge = ({ proto }: { proto: string }) => (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold border uppercase whitespace-nowrap ${PROTO_STYLES[proto] ?? "bg-slate-100 dark:bg-gray-500/10 border-slate-200 dark:border-gray-500/20 text-slate-600 dark:text-gray-400"}`}>
        {proto}
    </span>
);

// ── Category Badge ────────────────────────────────────────────────────────────
const CAT_STYLES: Record<string, string> = {
    "Remote Access": "bg-indigo-50 dark:bg-indigo-500/10 border-indigo-200 dark:border-indigo-500/20 text-indigo-700 dark:text-indigo-400",
    "Network Services": "bg-teal-50 dark:bg-teal-500/10 border-teal-200 dark:border-teal-500/20 text-teal-700 dark:text-teal-400",
    "File Access": "bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/20 text-amber-700 dark:text-amber-400",
    "Web Access": "bg-sky-50 dark:bg-sky-500/10 border-sky-200 dark:border-sky-500/20 text-sky-700 dark:text-sky-400",
    "Tunneling": "bg-violet-50 dark:bg-violet-500/10 border-violet-200 dark:border-violet-500/20 text-violet-700 dark:text-violet-400",
    "Web Proxy": "bg-emerald-50 dark:bg-emerald-500/10 border-emerald-200 dark:border-emerald-500/20 text-emerald-700 dark:text-emerald-400",
    "Email": "bg-pink-50 dark:bg-pink-500/10 border-pink-200 dark:border-pink-500/20 text-pink-700 dark:text-pink-400",
    "Authentication": "bg-red-50 dark:bg-red-500/10 border-red-200 dark:border-red-500/20 text-red-700 dark:text-red-400",
    "VoIP": "bg-lime-50 dark:bg-lime-500/10 border-lime-200 dark:border-lime-500/20 text-lime-700 dark:text-lime-400",
    "Database": "bg-cyan-50 dark:bg-cyan-500/10 border-cyan-200 dark:border-cyan-500/20 text-cyan-700 dark:text-cyan-400",
};

const CatBadge = ({ category }: { category: string }) => {
    if (!category) return <span className="text-slate-400 dark:text-gray-600">—</span>;
    return (
        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold border whitespace-nowrap ${CAT_STYLES[category] ?? "bg-slate-100 dark:bg-gray-500/10 border-slate-200 dark:border-gray-500/20 text-slate-600 dark:text-gray-400"}`}>
            {category}
        </span>
    );
};

// ── Port Chips ────────────────────────────────────────────────────────────────
const PortChips = ({ ports, color }: { ports: string[]; color: string }) => {
    if (ports.length === 0) return <span className="text-slate-400 dark:text-gray-600">—</span>;
    return (
        <div className="flex flex-wrap gap-1">
            {ports.map((p, i) => (
                <span key={i} className={`font-mono px-1.5 py-0.5 text-[9px] font-bold border rounded ${color}`}>
                    {p}
                </span>
            ))}
        </div>
    );
};

// ── Sort Icon ─────────────────────────────────────────────────────────────────
const SortIcon = ({ column, sortConfig }: { column: string; sortConfig: { key: string; direction: "asc" | "desc" } | null }) => {
    if (sortConfig?.key !== column) return (
        <div className="opacity-0 group-hover:opacity-40 transition-opacity"><ArrowUp size={10} className="text-slate-400" /></div>
    );
    return sortConfig.direction === "asc"
        ? <ArrowUp size={10} className="text-blue-600 dark:text-blue-400" />
        : <ArrowDown size={10} className="text-blue-600 dark:text-blue-400" />;
};

// ── Detail Row ────────────────────────────────────────────────────────────────
const renderDetailRow = (label: string, value: any, color = "text-slate-800 dark:text-slate-200") => (
    <div key={label} className="flex justify-between items-center py-1 border-b border-slate-100 dark:border-gray-700/40 last:border-0 hover:bg-slate-50 dark:hover:bg-gray-700/20 px-1 -mx-1 rounded transition-colors">
        <span className="text-[9px] font-bold text-slate-500 dark:text-gray-400 uppercase tracking-widest">{label}</span>
        <span className={`text-[10px] font-black ${color} text-right max-w-[65%] truncate`} title={String(value ?? "")}>
            {value !== null && value !== undefined && value !== "" ? String(value) : "—"}
        </span>
    </div>
);

// ── Detail View ───────────────────────────────────────────────────────────────
function ServiceObjectDetail({ item, onBack }: { item: ServiceObjectItem; onBack: () => void }) {
    const d = parseData(item.data);
    const tcpPorts = d ? fmtPorts(d["tcp-portrange"]) : [];
    const udpPorts = d ? fmtPorts(d["udp-portrange"]) : [];
    const sctpPorts = d ? fmtPorts(d["sctp-portrange"]) : [];

    return (
        <div className="space-y-3 animate-fade-in">

            {/* Header */}
            <div className="flex items-center gap-3 bg-slate-50 dark:bg-gray-800/40 border border-slate-200 dark:border-gray-700 p-2.5 rounded-xl shadow-sm">
                <button
                    onClick={onBack}
                    className="p-1.5 bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/30 rounded-md text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-white hover:bg-blue-100 dark:hover:bg-blue-500/20 transition-all group shrink-0"
                >
                    <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
                </button>
                <div className="bg-blue-100 dark:bg-blue-500/10 p-1.5 rounded-md border border-blue-200 dark:border-blue-500/20 shrink-0">
                    <Layers className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-sm font-bold text-slate-900 dark:text-white">{item.name}</h2>
                    {d && <ProtoBadge proto={d.protocol} />}
                    {d && <CatBadge category={d.category} />}
                </div>
            </div>

            {!d ? (
                <p className="text-slate-500 dark:text-gray-500 text-xs p-4">Failed to parse service object data.</p>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

                    {/* Config */}
                    <div className="bg-white dark:bg-gray-800/40 border border-slate-200 dark:border-gray-700 p-3 rounded-lg shadow-sm">
                        <div className="flex items-center gap-1.5 mb-2 border-b border-slate-100 dark:border-gray-700 pb-1.5">
                            <Info className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                            <h4 className="text-[10px] font-black text-blue-600 dark:text-blue-400 uppercase tracking-widest">Configuration</h4>
                        </div>
                        <div className="space-y-0.5">
                            {renderDetailRow("Protocol", d.protocol)}
                            {renderDetailRow("Category", d.category || null)}
                            {renderDetailRow("Proxy", d.proxy, d.proxy === "enable" ? "text-emerald-600 dark:text-green-400" : "text-slate-400 dark:text-gray-500")}
                            {renderDetailRow("Helper", d.helper)}
                            {renderDetailRow("Session TTL", d["session-ttl"] !== "0" ? d["session-ttl"] : null)}
                            {renderDetailRow("IP Range", d.iprange !== "0.0.0.0" ? d.iprange : null)}
                            {renderDetailRow("FQDN", d.fqdn || null)}
                            {renderDetailRow("Fabric Object", d["fabric-object"])}
                            {d.icmptype !== undefined && renderDetailRow("ICMP Type", d.icmptype)}
                            {d.icmpcode !== undefined && d.icmpcode !== "" && renderDetailRow("ICMP Code", d.icmpcode)}
                        </div>
                        {d.comment && (
                            <div className="mt-3 pt-3 border-t border-slate-100 dark:border-gray-700">
                                <p className="text-[9px] font-bold text-slate-500 dark:text-gray-400 uppercase tracking-widest mb-1">Comment</p>
                                <p className="text-[10px] text-slate-700 dark:text-gray-300">{d.comment}</p>
                            </div>
                        )}
                    </div>

                    {/* Ports */}
                    <div className="bg-white dark:bg-gray-800/40 border border-slate-200 dark:border-gray-700 p-3 rounded-lg shadow-sm">
                        <div className="flex items-center gap-1.5 mb-2 border-b border-slate-100 dark:border-gray-700 pb-1.5">
                            <Activity className="w-3.5 h-3.5 text-cyan-600 dark:text-cyan-400" />
                            <h4 className="text-[10px] font-black text-cyan-600 dark:text-cyan-400 uppercase tracking-widest">Port Ranges</h4>
                        </div>
                        <div className="space-y-3">
                            <div>
                                <p className="text-[9px] font-bold text-cyan-600 dark:text-cyan-500/70 uppercase tracking-widest mb-1.5">TCP Ports</p>
                                <PortChips ports={tcpPorts} color="bg-cyan-50 dark:bg-cyan-500/10 border-cyan-200 dark:border-cyan-500/20 text-cyan-700 dark:text-cyan-300" />
                            </div>
                            <div>
                                <p className="text-[9px] font-bold text-green-600 dark:text-green-500/70 uppercase tracking-widest mb-1.5">UDP Ports</p>
                                <PortChips ports={udpPorts} color="bg-green-50 dark:bg-green-500/10 border-green-200 dark:border-green-500/20 text-green-700 dark:text-green-300" />
                            </div>
                            <div>
                                <p className="text-[9px] font-bold text-purple-600 dark:text-purple-500/70 uppercase tracking-widest mb-1.5">SCTP Ports</p>
                                <PortChips ports={sctpPorts} color="bg-purple-50 dark:bg-purple-500/10 border-purple-200 dark:border-purple-500/20 text-purple-700 dark:text-purple-300" />
                            </div>
                        </div>
                    </div>

                    {/* Timers & Meta */}
                    <div className="bg-white dark:bg-gray-800/40 border border-slate-200 dark:border-gray-700 p-3 rounded-lg shadow-sm">
                        <div className="flex items-center gap-1.5 mb-2 border-b border-slate-100 dark:border-gray-700 pb-1.5">
                            <Clock className="w-3.5 h-3.5 text-amber-600 dark:text-yellow-400" />
                            <h4 className="text-[10px] font-black text-amber-600 dark:text-yellow-400 uppercase tracking-widest">Timers</h4>
                        </div>
                        <div className="space-y-0.5">
                            {renderDetailRow("TCP RST Timer", d["tcp-rst-timer"] || null)}
                            {renderDetailRow("TCP Half-Open", d["tcp-halfopen-timer"] || null)}
                            {renderDetailRow("TCP Half-Close", d["tcp-halfclose-timer"] || null)}
                            {renderDetailRow("TCP Time-Wait", d["tcp-timewait-timer"] || null)}
                            {renderDetailRow("UDP Idle", d["udp-idle-timer"] || null)}
                        </div>
                        <div className="mt-4 pt-3 border-t border-slate-100 dark:border-gray-700">
                            <p className="text-[9px] font-bold text-slate-500 dark:text-gray-400 uppercase tracking-widest mb-1">UUID</p>
                            <p className="font-mono text-[9px] text-slate-400 dark:text-gray-500 break-all">{d.uuid}</p>
                            <div className="mt-2">
                                {renderDetailRow("UUID Index", d["uuid-idx"])}
                                {renderDetailRow("App Service Type", d["app-service-type"])}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function FirewallServiceObjects({ asset_id, client_id }: { asset_id: any, client_id: string }) {
    const [objects, setObjects] = useState<ServiceObjectItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [selected, setSelected] = useState<ServiceObjectItem | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(15);
    const [totalRecords, setTotalRecords] = useState(0);
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: "asc" | "desc" } | null>(null);

    useEffect(() => {
        const t = setTimeout(() => { setDebouncedSearch(searchTerm); setCurrentPage(1); }, 400);
        return () => clearTimeout(t);
    }, [searchTerm]);

    const fetchObjects = useCallback(async () => {
        setLoading(true);
        try {
            const queryObj: any = {};
            if (asset_id) queryObj["asset_id"] = asset_id;
            if (client_id) queryObj["client_id"] = client_id;
            if (debouncedSearch) {
                queryObj.$or = [{ name: { $ilike: `%${debouncedSearch}%` } }];
            }
            const params: any = {
                query: JSON.stringify(queryObj),
                skip: (currentPage - 1) * limit,
                limit
            };
            if (sortConfig) { params.sort_by = sortConfig.key; params.sort_order = sortConfig.direction; }

            const res = await baseapi.get("/firewall_service_objects", { params });
            if (res.data.status) {
                setObjects(res.data.data);
                setTotalRecords(res.data.total ?? res.data.data.length);
            } else showError("Failed to load service objects");
        } catch (e: any) { showError(e.message); }
        finally { setLoading(false); }
    }, [asset_id, debouncedSearch, currentPage, limit, sortConfig]);

    useEffect(() => {
        if (!selected) fetchObjects();
    }, [fetchObjects, selected]);

    const handleSort = (key: string) => {
        setSortConfig(prev =>
            prev?.key === key
                ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
                : { key, direction: "asc" }
        );
        setCurrentPage(1);
    };

    const totalPages = Math.ceil(totalRecords / limit);
    const startRecord = totalRecords === 0 ? 0 : (currentPage - 1) * limit + 1;
    const endRecord = Math.min(currentPage * limit, totalRecords);

    if (selected) return <ServiceObjectDetail item={selected} onBack={() => setSelected(null)} />;

    const Th = ({ label, col, right = false }: { label: string; col: string; right?: boolean }) => (
        <th
            className={`px-4 py-2 text-[10px] font-bold text-slate-700 dark:text-gray-100 uppercase tracking-widest cursor-pointer group whitespace-nowrap ${right ? "text-right" : ""}`}
            onClick={() => col === 'name' && handleSort(col)}
        >
            <div className={`flex items-center gap-1 ${right ? "justify-end" : ""}`}>
                {label}
                {col === 'name' && <SortIcon column={col} sortConfig={sortConfig} />}
            </div>
        </th>
    );

    return (
        <div className="flex flex-col bg-transparent text-slate-800 dark:text-gray-200">

            {/* Action Bar */}
            <div className="py-2 flex items-center justify-between gap-4 shrink-0 mb-2">
                <div className="flex items-center gap-2">
                    <div className="bg-blue-100 dark:bg-blue-600/20 p-1.5 rounded-lg border border-blue-200 dark:border-blue-500/30">
                        <Layers size={16} className="text-blue-600 dark:text-blue-400" />
                    </div>
                    <h1 className="text-lg font-bold text-slate-900 dark:text-white tracking-wide">Service Objects</h1>
                    <span className="text-[10px] font-bold text-slate-600 dark:text-gray-400 bg-slate-100 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 px-2 py-0.5 rounded">
                        {totalRecords} services
                    </span>
                </div>
                <div className="flex items-center gap-2">
                    <div className="relative w-56 group">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 dark:text-gray-500 group-focus-within:text-blue-600 dark:group-focus-within:text-blue-400 transition-colors" />
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search services..."
                            className="w-full bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-[11px] text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-all shadow-sm"
                        />
                    </div>
                    <button
                        onClick={fetchObjects}
                        disabled={loading}
                        className="p-1.5 bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg text-slate-600 dark:text-gray-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-50 dark:hover:bg-gray-700 transition-all group shrink-0 shadow-sm"
                    >
                        <RefreshCw size={14} className={loading ? "animate-spin" : "group-hover:text-blue-600 dark:group-hover:text-blue-400"} />
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="bg-white dark:bg-gray-800/40 border border-slate-200 dark:border-gray-700 rounded-xl overflow-hidden shadow-sm dark:shadow-2xl">
                <div className="overflow-x-auto custom-scroll">
                    <table className="w-full text-left text-xs border-collapse">
                        <thead className="bg-slate-50 dark:bg-gray-800/80 sticky top-0 z-10">
                            <tr className="border-b border-slate-200 dark:border-gray-700/50">
                                <Th label="Name" col="name" />
                                <Th label="Protocol" col="protocol" />
                                <Th label="Category" col="category" />
                                <Th label="TCP Ports" col="tcp-portrange" />
                                <Th label="UDP Ports" col="udp-portrange" />
                                <Th label="SCTP Ports" col="sctp-portrange" />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-gray-700/30">
                            {loading ? (
                                <tr>
                                    <td colSpan={6} className="py-16 text-center">
                                        <div className="flex items-center justify-center gap-2 text-slate-500 dark:text-gray-400">
                                            <Loader2 className="w-4 h-4 animate-spin text-blue-600 dark:text-blue-400" />
                                            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-600 dark:text-gray-300">Loading service objects...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : objects.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="py-16 text-center">
                                        <div className="flex flex-col items-center gap-2 opacity-60">
                                            <Layers size={24} className="text-slate-400 dark:text-gray-400" />
                                            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-gray-200">No service objects found</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                objects.map((item) => {
                                    const d = parseData(item.data);
                                    const tcpPorts = d ? fmtPorts(d["tcp-portrange"]) : [];
                                    const udpPorts = d ? fmtPorts(d["udp-portrange"]) : [];
                                    const sctpPorts = d ? fmtPorts(d["sctp-portrange"]) : [];

                                    return (
                                        <tr
                                            key={item.id}
                                            onClick={() => setSelected(item)}
                                            className="hover:bg-slate-50/80 dark:hover:bg-gray-700/50 transition-colors cursor-pointer"
                                            title="Click to view details"
                                        >
                                            {/* Name */}
                                            <td className="px-4 py-2.5 whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                    <div>
                                                        <p className="text-[11px] font-bold text-slate-900 dark:text-gray-100">{item.name.replace(/_/g, " ")}</p>
                                                        {d?.comment && (
                                                            <p className="text-[9px] text-slate-500 dark:text-gray-400 truncate max-w-[160px]">{d.comment}</p>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Protocol */}
                                            <td className="px-4 py-2.5 whitespace-nowrap">
                                                {d ? <ProtoBadge proto={d.protocol} /> : <span className="text-slate-400 dark:text-gray-600">—</span>}
                                            </td>

                                            {/* Category */}
                                            <td className="px-4 py-2.5 whitespace-nowrap">
                                                {d ? <CatBadge category={d.category} /> : <span className="text-slate-400 dark:text-gray-600">—</span>}
                                            </td>

                                            {/* TCP Ports */}
                                            <td className="px-4 py-2.5">
                                                <PortChips ports={tcpPorts} color="bg-cyan-50 dark:bg-cyan-500/10 border-cyan-200 dark:border-cyan-500/20 text-cyan-700 dark:text-cyan-300" />
                                            </td>

                                            {/* UDP Ports */}
                                            <td className="px-4 py-2.5">
                                                <PortChips ports={udpPorts} color="bg-green-50 dark:bg-green-500/10 border-green-200 dark:border-green-500/20 text-green-700 dark:text-green-300" />
                                            </td>

                                            {/* SCTP Ports */}
                                            <td className="px-4 py-2.5">
                                                <PortChips ports={sctpPorts} color="bg-purple-50 dark:bg-purple-500/10 border-purple-200 dark:border-purple-500/20 text-purple-700 dark:text-purple-300" />
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Footer */}
                <div className="flex items-center justify-between px-3 py-1.5 bg-slate-50 dark:bg-gray-800/40 border-t border-slate-200 dark:border-gray-700 shrink-0">
                    <div className="flex items-center gap-3">
                        <ItemsPerPageDropdown
                            value={limit}
                            onChange={(v) => { setLimit(v); setCurrentPage(1); }}
                        />
                        <span className="text-[9px] font-medium text-slate-500 dark:text-gray-400 tracking-widest">
                            <span className="text-slate-700 dark:text-gray-300">{startRecord}</span>
                            {" - "}
                            <span className="text-slate-700 dark:text-gray-300">{endRecord}</span>
                            <span className="mx-1 text-slate-400 dark:text-gray-600">/</span>
                            <span className="text-slate-800 dark:text-gray-300 font-bold">{totalRecords}</span>
                        </span>
                        {debouncedSearch && (
                            <button onClick={() => setSearchTerm("")}
                                className="text-[9px] text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-bold uppercase tracking-widest transition-colors">
                                Clear search
                            </button>
                        )}
                    </div>
                    <div className="flex items-center gap-1">
                        <button disabled={currentPage === 1} onClick={() => setCurrentPage(1)}
                            className="p-1 text-slate-600 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white disabled:opacity-20 bg-white dark:bg-gray-800/50 hover:bg-slate-100 dark:hover:bg-gray-700 border border-slate-200 dark:border-transparent rounded">
                            <ChevronsLeft size={14} />
                        </button>
                        <button disabled={currentPage === 1} onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            className="p-1 text-slate-600 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white disabled:opacity-20 bg-white dark:bg-gray-800/50 hover:bg-slate-100 dark:hover:bg-gray-700 border border-slate-200 dark:border-transparent rounded">
                            <ChevronLeft size={14} />
                        </button>
                        <div className="px-2 py-0.5 bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/20 text-blue-700 dark:text-blue-400 rounded text-[10px] font-bold mx-1">
                            {currentPage}
                        </div>
                        <button disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)}
                            className="p-1 text-slate-600 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white disabled:opacity-20 bg-white dark:bg-gray-800/50 hover:bg-slate-100 dark:hover:bg-gray-700 border border-slate-200 dark:border-transparent rounded">
                            <ChevronRight size={14} />
                        </button>
                        <button disabled={currentPage >= totalPages} onClick={() => setCurrentPage(totalPages)}
                            className="p-1 text-slate-600 dark:text-gray-400 hover:text-slate-900 dark:hover:text-white disabled:opacity-20 bg-white dark:bg-gray-800/50 hover:bg-slate-100 dark:hover:bg-gray-700 border border-slate-200 dark:border-transparent rounded">
                            <ChevronsRight size={14} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
